import { useMemo, useState } from "react";
import { Modal, Table, Tabs, Input, Tag, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { buildExcelPreview } from "../report/ReportExport";

const { Text } = Typography;

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const matchSearch = (row, keyword) =>
  !keyword ||
  Object.values(row).some((v) =>
    String(v ?? "")
      .toLowerCase()
      .includes(keyword),
  );

const PreviewExport = ({ open, close, data, getTaskStatus, title }) => {
  const [search, setSearch] = useState("");
  const keyword = search.trim().toLowerCase();

  const preview = useMemo(
    () => buildExcelPreview(data, getTaskStatus),
    [data, getTaskStatus],
  );

  // ─── Sheet รายงาน ───────────────────────────────────────────
  const reportColumns = useMemo(
    () =>
      preview.reportColumns.map((col) => {
        const isMoney = preview.currencyNames.has(col);
        return {
          title: col,
          dataIndex: col,
          key: col,
          width: isMoney ? 140 : col === "ลำดับ" ? 70 : undefined,
          align: isMoney ? "right" : col === "ลำดับ" ? "center" : "left",
          ellipsis: col === "ศาล",
          sorter: isMoney ? (a, b) => (a[col] || 0) - (b[col] || 0) : undefined,
          render: isMoney
            ? (v) => (
                <Text type={v < 0 ? "danger" : undefined}>{fmtMoney(v)}</Text>
              )
            : col === "สถานะ"
              ? (v) => <Tag color={v === "สำเร็จ" ? "green" : "blue"}>{v}</Tag>
              : undefined,
        };
      }),
    [preview],
  );

  const reportRows = useMemo(
    () => preview.reportRows.filter((r) => matchSearch(r, keyword)),
    [preview, keyword],
  );

  // ─── Sheet รายการรับชำระ ────────────────────────────────────
  const paymentColumns = useMemo(
    () =>
      preview.paymentColumns.map((col, idx) => ({
        title: col.title,
        dataIndex: idx,
        key: col.title,
        width: col.type === "money" ? 130 : col.type === "int" ? 80 : undefined,
        align:
          col.type === "money"
            ? "right"
            : col.type === "int"
              ? "center"
              : "left",
        render: col.type === "money" ? fmtMoney : undefined,
      })),
    [preview],
  );

  const paymentRows = useMemo(
    () =>
      preview.paymentRows
        .map((r) => ({ ...r }))
        .filter((r) => matchSearch(r, keyword)),
    [preview, keyword],
  );

  // รวมจากทุกแถว (หลังค้นหา) ไม่ใช่แค่หน้าที่แสดงอยู่ — summary ของ antd ส่งมาแค่ pageData
  const renderSummary = (columns, rows, isMoney, getValue) => (
    <Table.Summary>
      <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 600 }}>
        {columns.map((col, idx) => (
          <Table.Summary.Cell key={col.key} index={idx} align={col.align}>
            {idx === 0
              ? "รวม"
              : idx === 1
                ? `${rows.length} รายการ`
                : isMoney(col)
                  ? fmtMoney(
                      rows.reduce(
                        (s, r) => s + (Number(getValue(r, col)) || 0),
                        0,
                      ),
                    )
                  : null}
          </Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    </Table.Summary>
  );

  const tableProps = {
    size: "small",
    bordered: true,
    scroll: { x: "max-content" },
    pagination: {
      defaultPageSize: 50,
      showSizeChanger: true,
      pageSizeOptions: [20, 50, 100, 500],
      showTotal: (total) => `ทั้งหมด ${total} รายการ`,
    },
  };

  const tabItems = [
    {
      key: "report",
      label: `รายงาน (${reportRows.length})`,
      children: (
        <Table
          {...tableProps}
          rowKey="ลำดับ"
          columns={reportColumns}
          dataSource={reportRows}
          summary={() =>
            renderSummary(
              reportColumns,
              reportRows,
              (col) => preview.currencyNames.has(col.key),
              (r, col) => r[col.key],
            )
          }
        />
      ),
    },
    preview.showPayment && {
      key: "payment",
      label: `รายการรับชำระ (${paymentRows.length})`,
      children: (
        <Table
          {...tableProps}
          rowKey={(r) => r[0]}
          columns={paymentColumns}
          dataSource={paymentRows}
          summary={() =>
            renderSummary(
              paymentColumns,
              paymentRows,
              (col) => col.align === "right",
              (r, col) => r[col.dataIndex],
            )
          }
        />
      ),
    },
    {
      key: "status",
      label: "สรุปสถานะ",
      children: (
        <Table
          size="small"
          bordered
          pagination={false}
          style={{ maxWidth: 480 }}
          rowKey={(r) => r[0]}
          columns={[
            { title: "สถานะ", dataIndex: 0, key: "status" },
            {
              title: "จำนวน (คดี)",
              dataIndex: 1,
              key: "count",
              align: "right",
              width: 120,
            },
          ]}
          dataSource={preview.statusCounts}
          summary={() => (
            <Table.Summary.Row style={{ fontWeight: 600 }}>
              <Table.Summary.Cell index={0}>รวมทั้งหมด</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                {preview.reportRows.length}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <Modal
      title={
        <span style={{ fontSize: 16, fontWeight: 400 }}>
          Preview ข้อมูล{title ? ` - ${title}` : ""}
        </span>
      }
      open={open}
      onCancel={close}
      width="95vw"
      footer={null}
      keyboard
      centered
      destroyOnHidden
    >
      <Tabs
        items={tabItems}
        tabBarExtraContent={
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="ค้นหาเลขสัญญา, ชื่อ, ใบเสร็จ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
        }
      />
    </Modal>
  );
};

export default PreviewExport;
